import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import 'styles/toast.css';

interface ToastContextType {
  showToast: (message: string, type: 'info' | 'error', duration: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

interface ToastState {
  message: string;
  type: 'info' | 'error';
  duration: number;
  visible: boolean;
  hiding: boolean;
}

const EXIT_MS = 280;


export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastState>({
    message: '',
    type: 'info',
    duration: 3000,
    visible: false,
    hiding: false,
  });

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  };

  const dismiss = () => {
    clearTimers();
    setToast((prev) => ({ ...prev, hiding: true }));
    exitTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false, hiding: false }));
    }, EXIT_MS);
  };

  const showToast = (message: string, type: 'info' | 'error', duration: number) => {
    clearTimers();
    setToast({ message, type, duration, visible: true, hiding: false });
    hideTimerRef.current = setTimeout(dismiss, duration);
  };

  useEffect(() => clearTimers, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast.visible && (
        <div
          className={`toast ${toast.type}${toast.hiding ? ' hiding' : ''}`}
          onClick={dismiss}
          role="alert"
          aria-live="polite"
        >
          {toast.type === 'error'
            ? <WarningIcon width={18} height={18} aria-hidden />
            : <InfoIcon width={18} height={18} aria-hidden />
          }
          <span>{toast.message}</span>
        </div>
      )}
    </ToastContext.Provider>
  );
};
